import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Users, 
  Circle,
  Clock,
  RefreshCw,
  Activity,
  X,
  Phone,
  MessageSquare,
  Calendar,
  FileText,
  Trash2,
  Edit,
  ArrowRight,
  PhoneCall,
  Globe,
  Instagram,
  Facebook,
  LogIn,
  LogOut,
  CalendarDays,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";

const TeamActivityPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [filterDate, setFilterDate] = useState(null); // null = all time, Date = specific day
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (user && user.role !== "manager") {
      navigate("/dashboard");
      return;
    }
    fetchActivityData();
    
    const interval = setInterval(() => {
      fetchActivityData(true);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [user]);

  const fetchActivityData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/team/activity`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setActivityData(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch team activity:", error);
      if (!silent) toast.error("Failed to load team activity");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLogs = async (userId, date = null) => {
    try {
      setLogsLoading(true);
      let url = `${API}/team/activity/${userId}/logs`;
      if (date) {
        const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
        url += `?date=${dateStr}`;
      }
      const response = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUserLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch user logs:", error);
      toast.error("Failed to load activity logs");
      setUserLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleUserClick = (member) => {
    setSelectedUser(member);
    setFilterDate(null); // Reset filter when selecting new user
    fetchUserLogs(member.user_id, null);
  };

  const handleDateFilter = (date) => {
    setFilterDate(date);
    setDatePickerOpen(false);
    if (selectedUser) {
      fetchUserLogs(selectedUser.user_id, date);
    }
  };

  const clearDateFilter = () => {
    setFilterDate(null);
    if (selectedUser) {
      fetchUserLogs(selectedUser.user_id, null);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    navigate("/");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "created_lead":
        return <FileText className="w-4 h-4 text-emerald-500" />;
      case "updated_lead":
        return <Edit className="w-4 h-4 text-blue-500" />;
      case "deleted_lead":
        return <Trash2 className="w-4 h-4 text-red-500" />;
      case "logged_call":
        return <Phone className="w-4 h-4 text-indigo-500" />;
      case "logged_text":
        return <MessageSquare className="w-4 h-4 text-purple-500" />;
      case "logged_meeting":
        return <Calendar className="w-4 h-4 text-amber-500" />;
      case "scheduled_call":
        return <PhoneCall className="w-4 h-4 text-cyan-500" />;
      case "changed_status":
        return <ArrowRight className="w-4 h-4 text-orange-500" />;
      case "clicked_phone":
        return <Phone className="w-4 h-4 text-emerald-500" />;
      case "clicked_website":
        return <Globe className="w-4 h-4 text-blue-500" />;
      case "clicked_instagram":
        return <Instagram className="w-4 h-4 text-pink-500" />;
      case "clicked_facebook":
        return <Facebook className="w-4 h-4 text-blue-600" />;
      case "logged_in":
        return <LogIn className="w-4 h-4 text-emerald-600" />;
      case "logged_out":
        return <LogOut className="w-4 h-4 text-slate-500" />;
      case "came_online":
        return <Circle className="w-4 h-4 text-emerald-500 fill-emerald-500" />;
      case "went_inactive":
        return <Circle className="w-4 h-4 text-slate-400" />;
      default:
        return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatLogTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const logDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
    
    if (logDate.getTime() === today.getTime()) {
      return `Today, ${timeStr}`;
    } else if (logDate.getTime() === yesterday.getTime()) {
      return `Yesterday, ${timeStr}`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + `, ${timeStr}`;
    }
  };

  // Format last seen timestamp in user's local timezone (12-hour format with AM/PM)
  const formatLastSeen = (member) => {
    if (!member.last_active) return "Never";
    
    // If user is online, use the backend's relative time (already accurate)
    if (member.is_online) {
      return member.last_seen_text;
    }
    
    // For offline users, format in local timezone with 12-hour format
    const date = new Date(member.last_active);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastSeenDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    
    if (lastSeenDate.getTime() === today.getTime()) {
      return `Today at ${timeStr}`;
    } else if (lastSeenDate.getTime() === yesterday.getTime()) {
      return `Yesterday at ${timeStr}`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ` at ${timeStr}`;
    }
  };

  const formatFilterDateLabel = (date) => {
    if (!date) return "All Time";
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const filterDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (filterDay.getTime() === today.getTime()) {
      return "Today";
    } else if (filterDay.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const formatLogDescription = (log) => {
    const leadName = log.details?.lead_name || "a lead";
    switch (log.action) {
      case "created_lead":
        return `Created lead "${leadName}"`;
      case "updated_lead":
        return `Updated lead "${leadName}"`;
      case "deleted_lead":
        return `Deleted lead "${leadName}"`;
      case "logged_call":
        return `Logged ${log.details?.count || 1} call${log.details?.count > 1 ? 's' : ''} on "${leadName}"`;
      case "logged_text":
        return `Logged ${log.details?.count || 1} text${log.details?.count > 1 ? 's' : ''} on "${leadName}"`;
      case "logged_meeting":
        return `Logged ${log.details?.count || 1} meeting${log.details?.count > 1 ? 's' : ''} on "${leadName}"`;
      case "scheduled_call":
        return `Scheduled call for "${leadName}"`;
      case "changed_status":
        return `Changed "${leadName}" from ${log.details?.from_status} to ${log.details?.to_status}`;
      case "clicked_phone":
        return `Called phone for "${leadName}"`;
      case "clicked_website":
        return `Opened website for "${leadName}"`;
      case "clicked_instagram":
        return `Opened Instagram for "${leadName}"`;
      case "clicked_facebook":
        return `Opened Facebook for "${leadName}"`;
      case "logged_in":
        return "Logged in";
      case "logged_out":
        return "Logged out";
      case "came_online":
        return "Came back online";
      case "went_inactive":
        return "Went inactive";
      default:
        return log.action_label || log.action;
    }
  };

  if (loading && !activityData) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
        <main className="flex-1 ml-64 p-8">
          <div className="flex items-center justify-center h-full">
            <div className="spinner w-8 h-8 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
          </div>
        </main>
      </div>
    );
  }

  const onlineUsers = activityData?.users?.filter(u => u.is_online) || [];
  const offlineUsers = activityData?.users?.filter(u => !u.is_online) || [];

  const UserCard = ({ member, isOnline }) => (
    <div 
      onClick={() => handleUserClick(member)}
      className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-all hover:scale-[1.02] ${
        isOnline 
          ? "bg-emerald-50 border border-emerald-100 hover:bg-emerald-100" 
          : "bg-slate-50 border border-slate-100 hover:bg-slate-100"
      } ${selectedUser?.user_id === member.user_id ? "ring-2 ring-indigo-500" : ""}`}
      data-testid={`${isOnline ? 'online' : 'offline'}-user-${member.user_id}`}
    >
      <div className="relative">
        <Avatar className={`h-10 w-10 ${!isOnline ? 'opacity-75' : ''}`}>
          <AvatarImage src={member.picture} />
          <AvatarFallback className={`font-medium ${
            isOnline ? "bg-emerald-200 text-emerald-800" : "bg-slate-200 text-slate-600"
          }`}>
            {getInitials(member.name)}
          </AvatarFallback>
        </Avatar>
        {isOnline && (
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white"></span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isOnline ? 'text-slate-900' : 'text-slate-700'}`}>
          {member.name}
        </p>
        <Badge 
          variant="secondary" 
          className={`text-xs ${!isOnline ? 'opacity-75' : ''} ${
            member.role === "manager" 
              ? "bg-amber-100 text-amber-700" 
              : "bg-indigo-100 text-indigo-700"
          }`}
        >
          {member.role === "manager" ? "Manager" : "Sales Rep"}
        </Badge>
      </div>
      <div className="text-right">
        <p className={`text-xs font-medium ${isOnline ? 'text-emerald-600' : 'text-slate-400'}`}>
          {formatLastSeen(member)}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="team-activity-page">
      <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
      
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 
              className="text-3xl font-bold text-slate-900 flex items-center gap-3"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <Activity className="w-8 h-8 text-indigo-600" />
              Team Activity
            </h1>
            <p className="text-slate-500 mt-1">
              Monitor your team's online status and activity • Click a user to see their activity feed
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                fetchActivityData();
                if (selectedUser) {
                  fetchUserLogs(selectedUser.user_id, filterDate);
                }
              }}
              disabled={loading || logsLoading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading || logsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Left Side - User Lists */}
          <div className={`${selectedUser ? 'w-1/2' : 'w-full'} transition-all`}>
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="border-slate-100 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Online</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {activityData?.online_count || 0}
                      </p>
                    </div>
                    <Circle className="w-4 h-4 text-emerald-600 fill-emerald-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-100 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Offline</p>
                      <p className="text-2xl font-bold text-slate-600">
                        {(activityData?.total_count || 0) - (activityData?.online_count || 0)}
                      </p>
                    </div>
                    <Circle className="w-4 h-4 text-slate-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-100 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Total</p>
                      <p className="text-2xl font-bold text-indigo-600">
                        {activityData?.total_count || 0}
                      </p>
                    </div>
                    <Users className="w-4 h-4 text-indigo-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Online Users */}
            {onlineUsers.length > 0 && (
              <Card className="border-slate-100 shadow-sm mb-4">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />
                    Online Now ({onlineUsers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className={`grid gap-3 ${selectedUser ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                    {onlineUsers.map((member) => (
                      <UserCard key={member.user_id} member={member} isOnline={true} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Offline Users */}
            <Card className="border-slate-100 shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Clock className="w-3 h-3 text-slate-400" />
                  {offlineUsers.length > 0 ? `Offline (${offlineUsers.length})` : "All Users"}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {offlineUsers.length === 0 && onlineUsers.length === 0 ? (
                  <div className="text-center py-6 text-slate-400">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No team members found</p>
                  </div>
                ) : offlineUsers.length === 0 ? (
                  <div className="text-center py-6 text-emerald-600">
                    <Circle className="w-10 h-10 mx-auto mb-2 fill-emerald-500" />
                    <p className="text-sm font-medium">Everyone is online!</p>
                  </div>
                ) : (
                  <div className={`grid gap-3 ${selectedUser ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                    {offlineUsers.map((member) => (
                      <UserCard key={member.user_id} member={member} isOnline={false} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Activity Log Panel */}
          {selectedUser && (
            <div className="w-1/2">
              <Card className="border-slate-100 shadow-sm sticky top-8">
                <CardHeader className="pb-3 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={selectedUser.picture} />
                        <AvatarFallback className={`font-medium ${
                          selectedUser.is_online ? "bg-emerald-200 text-emerald-800" : "bg-slate-200 text-slate-600"
                        }`}>
                          {getInitials(selectedUser.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900">
                          {selectedUser.name}
                        </CardTitle>
                        <p className="text-xs text-slate-500">{selectedUser.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUser(null)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Activity Feed
                    </h3>
                    <div className="flex items-center gap-2">
                      {filterDate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearDateFilter}
                          className="text-xs text-slate-500 hover:text-slate-700 h-7 px-2"
                        >
                          Clear
                        </Button>
                      )}
                      <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs font-medium"
                          >
                            <CalendarDays className="w-3.5 h-3.5 mr-1.5" />
                            {formatFilterDateLabel(filterDate)}
                            <ChevronDown className="w-3 h-3 ml-1.5 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end">
                          <div className="p-2 border-b">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7"
                                onClick={() => handleDateFilter(new Date())}
                              >
                                Today
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7"
                                onClick={() => {
                                  const yesterday = new Date();
                                  yesterday.setDate(yesterday.getDate() - 1);
                                  handleDateFilter(yesterday);
                                }}
                              >
                                Yesterday
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs h-7"
                                onClick={() => handleDateFilter(null)}
                              >
                                All Time
                              </Button>
                            </div>
                          </div>
                          <CalendarComponent
                            mode="single"
                            selected={filterDate}
                            onSelect={handleDateFilter}
                            disabled={(date) => date > new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  <div className="max-h-[500px] overflow-y-auto">
                    {logsLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="spinner w-6 h-6 border-3 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
                      </div>
                    ) : userLogs.length === 0 ? (
                      <div className="text-center py-12 text-slate-400">
                        <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{filterDate ? "No activity on this day" : "No activity recorded yet"}</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {userLogs.map((log, index) => (
                          <div 
                            key={log.log_id || index}
                            className="flex items-start gap-3 p-4 hover:bg-slate-50 transition-colors"
                          >
                            <div className="mt-0.5 p-1.5 rounded-full bg-slate-100">
                              {getActionIcon(log.action)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700">
                                {formatLogDescription(log)}
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5">
                                {formatLogTime(log.timestamp)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TeamActivityPage;
